import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AttendanceEditModal = ({ isOpen, onClose, selectedPeriod, selectedEmployee }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [newRecord, setNewRecord] = useState({
    date: '',
    hours: '',
    store_code: '',
    notes: ''
  });

  // Store/Company options
  const storeOptions = [
    { code: 'M', name: 'Main St', full_name: 'Main Street' },
    { code: 'K', name: 'Kissena', full_name: 'Kissena' },
    { code: '39TH', name: '39th Ave', full_name: '39th Avenue' },
    { code: 'R', name: 'Roosevelt', full_name: 'Roosevelt' }
  ];

  useEffect(() => {
    if (isOpen && selectedPeriod && selectedEmployee) {
      fetchAttendanceData();
    }
  }, [isOpen, selectedPeriod, selectedEmployee]);

  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      // Fetch existing attendance records
      const { data: attendance, error: attendanceError } = await supabase
        .from('payroll_attendance')
        .select('*')
        .eq('employee_number', selectedEmployee.employee_number)
        .eq('payroll_number', selectedPeriod.payroll_number)
        .order('date', { ascending: true });

      if (attendanceError) throw attendanceError;

      // Fetch payroll summary
      const { data: summary, error: summaryError } = await supabase
        .from('payroll_summary')
        .select('*')
        .eq('employee_number', selectedEmployee.employee_number)
        .eq('payroll_number', selectedPeriod.payroll_number)
        .single();

      if (summaryError && summaryError.code !== 'PGRST116') {
        throw summaryError;
      }

      setAttendanceRecords(attendance || []);
      setPayrollSummary(summary || null);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      alert('Error loading attendance data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };


  const addAttendanceRecord = async () => {
    if (!newRecord.date || !newRecord.hours || !newRecord.store_code) {
      alert('Please fill in Date, Hours, and Store');
      return;
    }

    setSaving(true);
    try {
      const selectedStore = storeOptions.find(store => store.code === newRecord.store_code);
      
      // Insert new attendance record
      const { error: insertError } = await supabase
        .from('payroll_attendance')
        .insert({
          employee_number: selectedEmployee.employee_number,
          employee_name: selectedEmployee.employee_name,
          payroll_number: selectedPeriod.payroll_number,
          date: newRecord.date,
          hours: parseFloat(newRecord.hours),
          company: selectedStore?.full_name || null,
          department: null,
          store_code: selectedStore?.code || null,
          store_name: selectedStore?.full_name || null,
          day_name: new Date(newRecord.date).toLocaleDateString('en-US', { weekday: 'long' })
        });

      if (insertError) throw insertError;

      // Reset form
      setNewRecord({
        date: '',
        hours: '',
        store_code: '',
        notes: ''
      });

      // Refresh data and recalculate payroll summary
      await fetchAttendanceData();
      await recalculatePayrollSummary();

    } catch (error) {
      console.error('Error adding attendance record:', error);
      alert('Error adding attendance record: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteAttendanceRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this attendance record?')) return;

    try {
      const { error } = await supabase
        .from('payroll_attendance')
        .delete()
        .eq('id', recordId);

      if (error) throw error;

      // Refresh data and recalculate payroll summary
      await fetchAttendanceData();
      await recalculatePayrollSummary();
    } catch (error) {
      console.error('Error deleting attendance record:', error);
      alert('Error deleting attendance record: ' + error.message);
    }
  };

  const recalculatePayrollSummary = async () => {
    try {
      // Get updated attendance records
      const { data: attendance, error: attendanceError } = await supabase
        .from('payroll_attendance')
        .select('hours')
        .eq('employee_number', selectedEmployee.employee_number)
        .eq('payroll_number', selectedPeriod.payroll_number);

      if (attendanceError) throw attendanceError;

      // Calculate timesheet hours from attendance records
      const timesheetHours = attendance.reduce((sum, record) => sum + (record.hours || 0), 0);

      // Get existing benefit hours from payroll summary
      const benefitHours = (payrollSummary?.pto_hp || 0) + 
                          (payrollSummary?.sick_leave || 0) + 
                          (payrollSummary?.lunch || 0);

      // Calculate total hours including benefits
      const totalHours = timesheetHours + benefitHours;

      // Update or create payroll summary
      if (payrollSummary) {
        const { error: updateError } = await supabase
          .from('payroll_summary')
          .update({
            total_hours: totalHours
          })
          .eq('employee_number', selectedEmployee.employee_number)
          .eq('payroll_number', selectedPeriod.payroll_number);

        if (updateError) throw updateError;
      } else {
        // Create new payroll summary if it doesn't exist
        const { error: insertError } = await supabase
          .from('payroll_summary')
          .insert({
            employee_number: selectedEmployee.employee_number,
            employee_name: selectedEmployee.employee_name,
            payroll_number: selectedPeriod.payroll_number,
            total_hours: timesheetHours, // Only timesheet hours for new records (no benefits yet)
            company: selectedEmployee.companies?.[0]?.company || null
          });

        if (insertError) throw insertError;
      }

      // Refresh payroll summary data
      await fetchAttendanceData();

    } catch (error) {
      console.error('Error recalculating payroll summary:', error);
      alert('Error updating payroll summary: ' + error.message);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString();
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 max-h-[95vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Edit Attendance</h2>
              <p className="text-gray-600">
                {selectedEmployee?.employee_name} - {selectedPeriod?.period_label}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2">Loading attendance data...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Existing Attendance Records */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Existing Attendance Records</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {attendanceRecords.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No attendance records found</p>
                  ) : (
                    attendanceRecords.map((record) => (
                      <div key={record.id} className="border rounded p-3 bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium">{formatDate(record.date)}</div>
                            <div className="text-sm text-gray-600">
                              {record.day_name} - {record.hours}h
                              {record.store_code && (
                                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                  {record.store_code}
                                </span>
                              )}
                            </div>
                            {record.store_name && (
                              <div className="text-sm text-gray-500">{record.store_name}</div>
                            )}
                          </div>
                          <button
                            onClick={() => deleteAttendanceRecord(record.id)}
                            className="text-red-500 hover:text-red-700 ml-2"
                            title="Delete record"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Payroll Summary */}
                {payrollSummary && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
                    <h4 className="font-semibold text-blue-800 mb-2">Payroll Summary</h4>
                    <div className="grid grid-cols-1 gap-4 text-sm">
                      <div>
                        <span className="text-blue-700">Timesheet Hours:</span>
                        <span className="font-medium ml-2">{attendanceRecords.reduce((sum, r) => sum + (r.hours || 0), 0).toFixed(2)}</span>
                      </div>
                      {payrollSummary && (
                        <>
                          {payrollSummary.pto_hp > 0 && (
                            <div>
                              <span className="text-blue-700">PTO/Holiday:</span>
                              <span className="font-medium ml-2">{payrollSummary.pto_hp.toFixed(2)}</span>
                            </div>
                          )}
                          {payrollSummary.sick_leave > 0 && (
                            <div>
                              <span className="text-blue-700">Sick Leave:</span>
                              <span className="font-medium ml-2">{payrollSummary.sick_leave.toFixed(2)}</span>
                            </div>
                          )}
                          {payrollSummary.lunch > 0 && (
                            <div>
                              <span className="text-blue-700">Lunch:</span>
                              <span className="font-medium ml-2">{payrollSummary.lunch.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="border-t pt-2 mt-2">
                            <span className="text-blue-700 font-semibold">Total Hours:</span>
                            <span className="font-bold ml-2">{payrollSummary.total_hours?.toFixed(2) || 0}</span>
                          </div>
                        </>
                      )}
                      {payrollSummary.total_pay && (
                        <div>
                          <span className="text-blue-700 font-semibold">Total Pay:</span>
                          <span className="font-bold ml-2 text-lg">${payrollSummary.total_pay?.toFixed(2) || 0}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Add New Record Form */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Add New Attendance Record</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                    <input
                      type="date"
                      value={newRecord.date}
                      onChange={(e) => setNewRecord({...newRecord, date: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hours *</label>
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      max="24"
                      value={newRecord.hours}
                      onChange={(e) => setNewRecord({...newRecord, hours: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                      placeholder="8.0"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter total hours worked (e.g., 8.0, 8.25, 8.5)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Store *</label>
                    <select
                      value={newRecord.store_code}
                      onChange={(e) => setNewRecord({...newRecord, store_code: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Store</option>
                      {storeOptions.map(store => (
                        <option key={store.code} value={store.code}>{store.name}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={addAttendanceRecord}
                    disabled={saving || !newRecord.date || !newRecord.hours || !newRecord.store_code}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {saving && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    )}
                    {saving ? 'Adding Record...' : 'Add Attendance Record'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end mt-6 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceEditModal;